/**
 * The generic parameter form.
 *
 * This is the §1 acceptance test made visible: it knows the four parameter
 * KINDS declared in the exercise contract and nothing whatsoever about any
 * individual exercise. A new exercise ships a new file and a registration line,
 * and its settings appear here without this file being opened.
 *
 * If you are ever tempted to add `if (exerciseId === ...)` to this file, the
 * contract in packages/exercises/src/types.ts is missing something. Extend the
 * contract instead.
 *
 * Extracted from the Phase 2 preview screen, which proved the shape works
 * across three quite different exercises.
 */

import { useState } from 'react';
import type { ParamSpec, ParamValues, ValidationMessage } from '@pathways/exercises';
import { degToPx, displayUnitOf, fromDisplayValue, toDisplayValue } from '@pathways/exercises';
import { loadPxPerDegree, screenWidthFraction } from './calibration.js';
import styles from './ParamForm.module.css';

export interface ParamFormProps {
  /** Straight from `definition.params`. */
  params: Record<string, ParamSpec>;
  values: ParamValues;
  onChange: (key: string, value: number | string | boolean) => void;
  /** Anchored to individual fields by their `param` key. */
  messages: readonly ValidationMessage[];
  /** Unique per rendered instance, so labels bind to the right control. */
  idPrefix: string;
  disabled?: boolean;
}

export function ParamForm({
  params,
  values,
  onChange,
  messages,
  idPrefix,
  disabled = false,
}: ParamFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // The clinician's calibration, read once per render rather than per field.
  // It only feeds the "how much of the screen" readout — nothing prescribed
  // depends on it.
  const pxPerDegree = loadPxPerDegree();

  const entries = Object.entries(params);
  const common = entries.filter(([, spec]) => !spec.advanced);
  const advanced = entries.filter(([, spec]) => spec.advanced);

  const forField = (key: string) => messages.filter((m) => m.param === key);

  const render = ([key, spec]: [string, ParamSpec]) => (
    <ParamField
      key={key}
      name={key}
      spec={spec}
      value={values[key]}
      messages={forField(key)}
      idPrefix={idPrefix}
      disabled={disabled}
      pxPerDegree={pxPerDegree}
      onChange={(next) => onChange(key, next)}
    />
  );

  return (
    <div className={styles.form}>
      <div className={styles.grid}>{common.map(render)}</div>

      {advanced.length > 0 && (
        <div className={styles.advanced}>
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced((open) => !open)}
          >
            {showAdvanced ? '− ' : '+ '}
            {advanced.length} advanced setting{advanced.length === 1 ? '' : 's'}
          </button>
          {showAdvanced && <div className={styles.grid}>{advanced.map(render)}</div>}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// One control per parameter KIND. Four kinds, no exercises.
// =============================================================================

function ParamField({
  name,
  spec,
  value,
  messages,
  idPrefix,
  disabled,
  pxPerDegree,
  onChange,
}: {
  name: string;
  spec: ParamSpec;
  value: number | string | boolean | undefined;
  messages: readonly ValidationMessage[];
  idPrefix: string;
  disabled: boolean;
  pxPerDegree: number;
  onChange: (value: number | string | boolean) => void;
}) {
  const id = `${idPrefix}-${name}`;
  const notes = messages.length > 0 && (
    <ul className={styles.notes}>
      {messages.map((m) => (
        <li
          key={m.message}
          className={m.severity === 'blocking' ? styles.blocking : styles.advisory}
        >
          {/* Never colour alone — the severity is stated in words too, because
              a meaningful share of these patients' clinicians read on screens
              with the colour turned down. */}
          <strong>{m.severity === 'blocking' ? 'Will not run:' : 'Note:'}</strong>{' '}
          {m.message}
        </li>
      ))}
    </ul>
  );

  if (spec.kind === 'boolean') {
    return (
      <div className={styles.field}>
        <label className={styles.checkField} htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>
            <span className={styles.label}>{spec.label}</span>
            {spec.help && <span className={styles.help}>{spec.help}</span>}
          </span>
        </label>
        {notes}
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {spec.label}
        {spec.kind === 'number' && (
          <em className={styles.reading}>
            {String(toDisplayValue(spec, Number(value)))}
            {displayUnitOf(spec) ? ` ${displayUnitOf(spec)}` : ''}
          </em>
        )}
      </label>

      {/*
        The stepper works entirely in the unit the doctor reads. Bounds and step
        are converted with it, so a parameter stored in seconds and read in
        minutes steps by half-minutes rather than by an odd fraction.
      */}
      {spec.kind === 'number' && (
        <Stepper
          id={id}
          min={toDisplayValue(spec, spec.min)}
          max={toDisplayValue(spec, spec.max)}
          step={toDisplayValue(spec, spec.step)}
          value={toDisplayValue(spec, Number(value))}
          disabled={disabled}
          onChange={(shown) => onChange(fromDisplayValue(spec, shown))}
        />
      )}

      {spec.kind === 'enum' && (
        <select
          id={id}
          className={styles.input}
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {spec.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {spec.kind === 'color' && (
        <input
          id={id}
          className={styles.color}
          type="color"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {spec.kind === 'number' && spec.fromCentre && (
        <ScreenExtent value={Number(value)} pxPerDegree={pxPerDegree} />
      )}

      {spec.help && <span className={styles.help}>{spec.help}</span>}
      {notes}
    </div>
  );
}

/**
 * How much of the doctor's screen this movement actually uses.
 *
 * The clinical number is degrees and stays degrees — it describes the eye
 * movement, and it has to mean the same thing on his monitor and the patient's.
 * But "15 degrees" says nothing about whether the target will still be on the
 * screen, and at the top of the range it will not be. This connects the two
 * without changing either.
 *
 * Silent when the browser will not report a screen size, because a guess here
 * would be worse than saying nothing.
 */
function ScreenExtent({
  value,
  pxPerDegree,
}: {
  value: number;
  pxPerDegree: number;
}) {
  if (!Number.isFinite(value)) return null;

  const fraction = screenWidthFraction(degToPx(value, pxPerDegree));
  if (fraction === null) return null;

  if (fraction > 1) {
    return (
      <span className={styles.extentWarn}>
        Wider than this screen — it needs about {Math.round(fraction * 100)}% of
        the width, so the exercise would shrink it to fit and the patient would
        work at less than the amplitude prescribed here.
      </span>
    );
  }

  return (
    <span className={styles.extent}>
      Uses about {Math.round(fraction * 100)}% of screen width.
    </span>
  );
}

/**
 * Discrete plus/minus stepping. NOT a slider — DECISIONS.md 2026-08-20.
 *
 * A slider invites a value picked by hand position rather than by intent, it
 * cannot be nudged reliably by one step, and two prescriptions that were meant
 * to be identical end up a degree apart. Stepping by the parameter's own
 * declared `step` means every reachable value is one the exercise author
 * actually considered.
 */
export function Stepper({
  id,
  min,
  max,
  step,
  value,
  disabled = false,
  onChange,
}: {
  id: string;
  min: number;
  max: number;
  step: number;
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  // Steps are often fractional (0.1 deg). Snap to the step grid and round off
  // binary floating-point dust so the reading is 0.9, never 0.8999999999.
  const decimals = (String(step).split('.')[1] ?? '').length;
  const clamp = (next: number) =>
    Number(Math.min(max, Math.max(min, next)).toFixed(decimals));

  return (
    <div className={styles.stepper}>
      <button
        type="button"
        className={styles.stepButton}
        aria-label="Decrease"
        disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - step))}
      >
        −
      </button>
      <input
        id={id}
        className={styles.stepValue}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) onChange(clamp(next));
        }}
      />
      <button
        type="button"
        className={styles.stepButton}
        aria-label="Increase"
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + step))}
      >
        +
      </button>
    </div>
  );
}
